import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChartNoAxesColumn } from 'lucide-react';
import { DATE_TIME_FORMAT, formatDateSafe } from '@/utils/dateUtils';
import type { AnalysisEntry } from '@/types/documents';
import { cn } from '@/lib/utils';
import AnalysisTypeBadge from './AnalysisTypeBadge';

interface RelatedAnalysisPanelProps {
  entries: AnalysisEntry[];
  /** Rendered above the list with the analysis icon. Omit for a bare list. */
  heading?: string;
  showVersion?: boolean;
  className?: string;
}

const RelatedAnalysisPanel: React.FC<RelatedAnalysisPanelProps> = ({
  entries,
  heading,
  showVersion = false,
  className,
}) => {
  const { t, i18n } = useTranslation();

  if (entries.length === 0) return null;

  return (
    <div
      className={cn(
        'rounded-md bg-foreground/[0.04] px-3 py-2.5 dark:bg-foreground/[0.08]',
        className,
      )}
    >
      {heading && (
        <div className="mb-2 flex items-center gap-1.5 text-primary">
          <ChartNoAxesColumn className="size-3.5 shrink-0" strokeWidth={2} aria-hidden />
          <span className="revy-cjk text-xs font-medium leading-[1.5]">{heading}</span>
        </div>
      )}

      <ul className="flex flex-col gap-2.5">
        {entries.map((entry) => (
          <li key={entry.id} className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="flex items-center gap-2">
                <AnalysisTypeBadge type={entry.type} />
                {showVersion && (
                  <span className="revy-figure text-xs text-muted-foreground">v{entry.version}</span>
                )}
              </span>
              <time
                dateTime={entry.created_at || undefined}
                className="revy-figure text-xs text-muted-foreground"
              >
                {formatDateSafe(entry.created_at, DATE_TIME_FORMAT, { t, language: i18n.language })}
              </time>
            </div>
            <p className="revy-cjk text-xs leading-[1.5] text-muted-foreground">
              {t('documents.analysisHistoryView.createdBy', { name: entry.created_by })}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RelatedAnalysisPanel;
